import dayjs from 'dayjs';
import stateLookup from '../../data/stateLookup.json';

// Helper to check if an item is enabled based on enableWhen logic
const isItemEnabled = (item, enableWhenOk, extractItemProperties) => {
  if (!item) return true;
  const { enableWhenArrFromCode } = extractItemProperties(item);
  return enableWhenOk(item.enableWhen, enableWhenArrFromCode, item.enableBehavior);
};

// Lookup a value in stateLookup.json by key
const findStateLookupValues = (key) => {
  if (!key) return '';
  return stateLookup[key]?.state ?? '';
};

// Dynamically extract repeating group array from values and questionnaire
const extractRepeatingGroupArrayDynamic = (values, groupItems) => {
  const fieldMap = new Map();
  for (const item of groupItems.item) {
    let outKey = null;
    if (Array.isArray(item.code)) {
      const code = item.code.find((c) => c.system === 'backend:path' || c.system === 'backend:path:stateLookup');
      if (code && code.code) outKey = { code: code.code, system: code.system };
    }
    if (!outKey) outKey = { code: item.linkId, system: null };
    fieldMap.set(item.linkId, outKey);
  }
  const indexed = {};
  for (const [key, value] of Object.entries(values)) {
    const match = key.match(/^(.*?)-(\d+)$/) || key.match(/^(.*?)(?:\s-\s(\d+))?$/);
    if (match) {
      const fieldId = match[1];
      const idx = match[2] !== undefined ? Number.parseInt(match[2], 10) : 0;
      if (fieldMap.has(fieldId)) {
        if (!indexed[idx]) indexed[idx] = {};
        const filteredId = fieldMap.get(fieldId);
        indexed[idx][filteredId.code] =
          filteredId.system === 'backend:path:stateLookup' ? findStateLookupValues(value) : value;
      }
    }
  }
  // undefined values to  empty string
  return Object.values(indexed).map((obj) => {
    const newObj = {};
    for (const key of Object.keys(obj)) {
      newObj[key] = obj[key] === undefined ? '' : obj[key];
    }
    return newObj;
  });
};

// Helper to parse backend:path:staticText in 'path:value' format
const parseStaticBackendPath = (staticBackendPath) => {
  if (staticBackendPath && staticBackendPath.includes(':')) {
    const [childPath, textValue] = staticBackendPath.split(':');
    return { childPath, textValue };
  }
  return { childPath: null, textValue: null };
};

// Helper to get the code object for backend:path
const getBackendPathCode = (item) => {
  if (!Array.isArray(item?.code)) return null;
  const allowedSystems = new Set([
    'backend:path',
    'backend:path:staticText',
    'backend:path:stateLookup',
    'backend:path:storepath',
    'backend:path:extractId',
    'backend:path:calculate',
    'backend:path:selectedDropdownLabelAndBackendValueDiffers',
    'backend:path:toLowerCaseFirstLetter',
    'backend:path:toUpperCase',
    'backend:path:selectedRadioLabelAndBackendValueDiffers',
  ]);
  return item.code.filter((c) => typeof c.system === 'string' && allowedSystems.has(c.system)) || null;
};

const getCode = (item, system) =>
  Array.isArray(item?.code) ? item.code.find((c) => c.system === system) || null : null;

const getCodeValue = (item, system) => getCode(item, system)?.code ?? null;

const getItemControl = (item) =>
  (item?.extension || []).find((e) => e.url === 'http://hl7.org/fhir/StructureDefinition/questionnaire-itemControl')
    ?.valueCodeableConcept?.coding?.[0]?.code ?? null;

const isGroup = (it) => it?.type === 'group';

const isDisplay = (it) => it?.type === 'display';

const isPageGroup = (it) => isGroup(it) && getItemControl(it) === 'page';

const isEmpty = (v) => {
  if (v == null) return true;
  if (Array.isArray(v)) return v.length === 0;
  const s = String(v).trim();
  return s.length === 0 || s === 'undefined' || s === 'null';
};

const setByPath = (obj, path, value) => {
  if (!path) return;
  const parts = String(path).split('.');
  let cur = obj;
  for (let i = 0; i < parts.length; i += 1) {
    const key = parts[i];
    const arrayMatch = key.match(/^(\w+)\[(\d+)]$/);
    const last = i === parts.length - 1;
    if (arrayMatch) {
      const arrKey = arrayMatch[1];
      const arrIdx = Number(arrayMatch[2]);
      cur[arrKey] = cur[arrKey] || [];
      while (cur[arrKey].length <= arrIdx) {
        cur[arrKey].push({});
      }
      if (last) {
        cur[arrKey][arrIdx] = value;
      } else {
        cur = cur[arrKey][arrIdx];
      }
    } else if (last) {
      // If key is an array and value is object, push to array
      if (Array.isArray(cur[key]) && typeof value === 'object' && value !== null) {
        cur[key].push(value);
      } else {
        cur[key] = value;
      }
    } else {
      cur[key] = cur[key] ?? {};
      cur = cur[key];
    }
  }
};

const extractIdFromPath = (display, value) => {
  // If display is a number, ignore first 'display' letters from value
  const num = Number(display);
  if (!Number.isNaN(num) && typeof value === 'string') {
    return value.slice(num);
  }
  // fallback: return value as is
  return value;
};

const calculateValue = (dob) => {
  if (!dob) return '';
  if (!dayjs(dob).isValid()) return '';

  const age = dayjs().diff(dob, 'year');
  return age > 17 ? 'No' : 'Yes';
};

const switchCaseForToSetBackendPathValues = (it, values, out, context, fetchedCode) => {
  if (!fetchedCode || !fetchedCode.code || !fetchedCode.system) {
    return;
  }
  const { system, code, display } = fetchedCode;
  switch (system) {
    case 'backend:path': {
      const v = values[it.linkId];
      setByPath(
        out,
        code,
        !isEmpty(v) ? (it.type === 'date' ? v.format(display) : v) : it.type === 'date' ? dayjs().format(display) : ''
      );
      break;
    }
    case 'backend:path:staticText': {
      const { childPath, textValue } = parseStaticBackendPath(code);
      if (childPath) {
        setByPath(out, childPath, textValue);
      }
      break;
    }
    case 'backend:path:toLowerCaseFirstLetter': {
      const firstLetterLowerCase = values[it.linkId]
        ? values[it.linkId][0].toLowerCase() + values[it.linkId].slice(1)
        : '';
      setByPath(out, code, firstLetterLowerCase);
      break;
    }
    case 'backend:path:toUpperCase': {
      const toUpper = values[it.linkId] ? values[it.linkId].toUpperCase() : '';
      setByPath(out, code, toUpper);
      break;
    }
    case 'backend:path:stateLookup': {
      const stateValue = findStateLookupValues(values[it.linkId]);
      setByPath(out, code, stateValue);
      break;
    }
    case 'backend:path:extractId': {
      const extractedValue = extractIdFromPath(display, values[it.linkId]);
      setByPath(out, code, extractedValue);
      break;
    }
    case 'backend:path:calculate': {
      const calculatedValue = calculateValue(values[display]);
      setByPath(out, code, calculatedValue);
      break;
    }
    case 'backend:path:selectedRadioLabelAndBackendValueDiffers': {
      const [uiValue, backendValue] = display.split(':');
      if (values[it.linkId] === uiValue) {
        setByPath(out, code, backendValue);
      }
      break;
    }
    case 'backend:path:selectedDropdownLabelAndBackendValueDiffers': {
      // display example: "Yes:No"
      // code example: "allegedPerson[0].memberSelfInflicted"
      // values[it.linkId] is the UI value
      // Split at the first colon
      const firstColonIdx = code.indexOf(':');
      const backendPath = code.slice(0, firstColonIdx); // "allegedPerson[0].memberSelfInflicted"
      const valuesPart = code.slice(firstColonIdx + 1); // "Yes:No"
      // Split valuesPart by colon
      const [backendValueIfYes, backendValueIfNo] = valuesPart.split(':');
      const valueFromUI = values[it.linkId];

      // Check for array or string
      const isYes = Array.isArray(valueFromUI) ? valueFromUI.includes(display) : valueFromUI === display;

      // If UI value matches first part, use it; else use second part
      const backendValue = isYes ? backendValueIfYes : backendValueIfNo;
      setByPath(out, backendPath, backendValue === 'null' ? null : backendValue);
      break;
    }
    case 'backend:path:storepath': {
      // Support format: 'submitterData.NPI:providerStore.npi'
      const [payloadPath, storePath] = code.split(':');
      if (payloadPath && storePath) {
        // Traverse storePath (e.g., providerStore.npi)
        const storeParts = storePath.split('.');
        let storeObj = context;
        for (const part of storeParts) {
          if (storeObj && typeof storeObj === 'object') {
            storeObj = storeObj[part];
          } else {
            storeObj = undefined;
            break;
          }
        }
        const storeValue = storeObj;
        setByPath(out, payloadPath, !isEmpty(storeValue) ? storeValue : '');
      }
      break;
    }
    default: {
      // Default case: fallback logic for unknown backendPathCode
      // Optionally, set value as empty string or log for debugging
      if (
        it.linkId &&
        typeof system === 'string' &&
        (system === 'backend:path' ||
          system === 'backend:path:staticText' ||
          system === 'backend:path:stateLookup' ||
          system === 'backend:path:storepath' ||
          system === 'backend:path:extractId' ||
          system === 'backend:path:calculate' ||
          system === 'backend:path:selectedDropdownLabelAndBackendValueDiffers' ||
          system === 'backend:path:toLowerCaseFirstLetter' ||
          system === 'backend:path:toUpperCase' ||
          system === 'backend:path:selectedRadioLabelAndBackendValueDiffers')
      ) {
        const v = values[it.linkId];
        setByPath(out, code, !isEmpty(v) ? v : '');
      }
      break;
    }
  }
};

// Parse keys like 'questions[].questionID' and 'questions[].answerID[]'
const parseKey = (rawKey) => {
  // e.g. 'questions[].questionID' => { parent: 'questions', key: 'questionID', isArray: true }
  const match = rawKey.match(/^(\w+)\[]\.(\w+)(\[])?$/);
  if (match) {
    return {
      parent: match[1],
      key: match[2],
      isArray: !!match[3],
    };
  }
  // e.g. 'questions[].questionID' (no trailing [])
  const match2 = rawKey.match(/^(\w+)\[]\.(\w+)$/);
  if (match2) {
    return {
      parent: match2[1],
      key: match2[2],
      isArray: false,
    };
  }
  // e.g. 'questions[].answerID[]'
  const match3 = rawKey.match(/^(\w+)\[]\.(\w+)\[]$/);
  if (match3) {
    return {
      parent: match3[1],
      key: match3[2],
      isArray: true,
    };
  }
  // fallback: just key
  return { parent: null, key: rawKey, isArray: false };
};

const setQuestionsBackendPathValues = (it, values, out) => {
  const isIncidentQuestions = Array.isArray(it.code) && it.code.some((c) => c.system === 'backend:questionConfigs');
  if (isIncidentQuestions) {
    let v = values[it.linkId];
    const selectedValues = !isEmpty(v) ? (Array.isArray(v) ? v : [v]) : [];
    // Find all backend:questionConfigs codes
    const configs = it.code.filter((c) => c.system === 'backend:questionConfigs');
    const questionObjs = [];
    // Get template keys for output
    const rawQuestionIdKey = getCodeValue(it, 'backend:path:questionID') || 'questionID';
    const rawQuestionCategoryKey = getCodeValue(it, 'backend:path:questionCategory') || 'questionCategory';
    const rawAnswerIdKey = getCodeValue(it, 'backend:path:answerID') || 'answerID';
    const qidMeta = parseKey(rawQuestionIdKey);
    const qcatMeta = parseKey(rawQuestionCategoryKey);
    const aidMeta = parseKey(rawAnswerIdKey);
    const pushAllCodesToOneObject = it.code.some((c) => c.display === 'pushAllCodesToOneObject');
    // Build the output object dynamically
    const parentKey = qidMeta.parent || 'questions';
    for (const config of configs) {
      // config.display is the answer text, config.code is the answerId

      if (config.display.includes('${')) {
        const [newLinkeStart, format] = config.display.split(':');
        if (newLinkeStart || format) {
          const dateRangeLinkId = newLinkeStart.replaceAll(/\${[^}]+}/g, it.linkId);
          v = values[dateRangeLinkId]?.format(format);
        }
      }

      if (it.type === 'date' && !isEmpty(v)) {
        v = v.format(config.display);
      }

      const isSelected = selectedValues.includes(config.display);
      // Parse config.code: "Q1:A14:QOC Information:[]" or "Q7:A1-Other-${value}:QOC Information"
      const [questionIdRaw, answerIdRaw, questionCategoryRaw] = config.code.split(':');
      // Remove [] from answerIdRaw and questionCategoryRaw
      let answerId = answerIdRaw ? answerIdRaw.replaceAll('[]', '') : '';
      const questionCategoryClean = questionCategoryRaw
        ? questionCategoryRaw.replaceAll('[]', '')
        : questionCategoryRaw;
      // If answerId contains ${value}, replace with linkId value
      let isOtherValue = false;
      if (answerId && answerId.includes('${')) {
        // Replace all ${...} patterns with linkValue
        const linkValue = Array.isArray(v) ? v.join(',') : v;
        answerId = answerId.replaceAll(/\${[^}]+}/g, linkValue);
        isOtherValue = !!linkValue;
      }
      // Always concatenate questionIdRaw and answerId for final answerId
      const finalAnswerId = `${questionIdRaw}${answerId}`;

      const minimalObj = {
        [qidMeta.key]: questionIdRaw,
        [qcatMeta.key]: questionCategoryClean,
        [aidMeta.key]: aidMeta.isArray ? [] : '',
      };

      const fullObj = {
        [qidMeta.key]: questionIdRaw,
        [qcatMeta.key]: questionCategoryClean,
        [aidMeta.key]: aidMeta.isArray ? [finalAnswerId] : finalAnswerId,
      };

      if (isSelected || isOtherValue) {
        questionObjs.push(fullObj);
      } else {
        if (config.code?.includes('[]')) {
          questionObjs.push(minimalObj);
        }
        if (isEmpty(v) && config.code?.includes('[1]')) {
          const alreadyExists = questionObjs.some((obj) => obj[qidMeta.key] === questionIdRaw);
          if (!alreadyExists) {
            questionObjs.push(minimalObj);
          }
        }
      }
    }

    // For other items, skip answerOption mapping
    if (questionObjs.length > 0) {
      out[parentKey] = out[parentKey] || [];
      // For other items, skip answerOption mapping
      if (pushAllCodesToOneObject) {
        // Combine all answerIds into one array
        const combinedObj = { ...questionObjs[0] };
        combinedObj[aidMeta.key] = questionObjs.flatMap((obj) =>
          Array.isArray(obj[aidMeta.key]) ? obj[aidMeta.key] : [obj[aidMeta.key]]
        );
        // Check if an object with the same questionId already exists
        const existingIdx = out[parentKey].findIndex((obj) => obj[qidMeta.key] === combinedObj[qidMeta.key]);
        if (existingIdx !== -1) {
          // Merge answerIds into the existing object
          const existingObj = out[parentKey][existingIdx];
          existingObj[aidMeta.key] = [
            ...new Set([
              ...(Array.isArray(existingObj[aidMeta.key]) ? existingObj[aidMeta.key] : [existingObj[aidMeta.key]]),
              ...combinedObj[aidMeta.key],
            ]),
          ];
        } else {
          out[parentKey].push(combinedObj);
        }
      } else {
        out[parentKey].push(...questionObjs);
      }
    }
  }
};

const setAttachmentsBackendPath = (it, values, out) => {
  const attachmentsCodeObj = Array.isArray(it.code)
    ? it.code.find((c) => c.system === 'backend:path:attachments')
    : null;
  if (attachmentsCodeObj) {
    // Find all backend:path codes for documents[] fields in this item
    const docFields = [];
    let documentsKey = '';
    if (attachmentsCodeObj.code && attachmentsCodeObj.code.endsWith('[]')) {
      documentsKey = attachmentsCodeObj.code.replace('[]', '');
    }
    for (const c of it.code) {
      if (c.system === 'backend:path:attachment') {
        docFields.push({ code: c.code, display: c.display });
      }
    }

    // Get linked attachment object from values using linkId
    let attachments = [];
    if (it.linkId && values[it.linkId]) {
      attachments = values[it.linkId];
    }
    if (!out[documentsKey]) out[documentsKey] = [];
    if (Array.isArray(attachments) && attachments.length > 0) {
      for (const fileObj of attachments) {
        if (!fileObj.errorMessage || String(fileObj.errorMessage).trim() === '') {
          const docObj = {};
          for (const field of docFields) {
            // Map display to value from fileObj
            let value = '';
            if (field.display) {
              // e.g. file.name, file.type, metadata.uploadContext
              const [objKey, fileKey] = field.display.split('.');
              value = fileObj[objKey]?.[fileKey] ?? '';
            }
            // Set by path
            const key = field.code.split('.').pop().replace('[]', '');
            docObj[key] = value;
          }
          out[documentsKey].push(docObj);
        }
      }
    }
  }
};

// Refactored post-processing logic for repeating group arrays
const postProcessRepeatingGroups = (out) => {
  for (const key of Object.keys(out)) {
    if (Array.isArray(out[key]) && /^\d+$/.test(key)) {
      const firstObj = out[key][0];
      if (firstObj) {
        const newGroupKey = Object.keys(firstObj).find((k) => /\w+\[]\./.test(k));
        if (newGroupKey) {
          const baseKey = newGroupKey.split('[')[0];
          let newArr = out[key].map((obj) => {
            const newObj = {};
            for (const k of Object.keys(obj)) {
              const cleanKey = k.replace(/.*\[]\.?/, '').replace(/^\./, '');
              if (cleanKey.includes('.')) {
                const parts = cleanKey.split('.');
                let cur = newObj;
                for (let i = 0; i < parts.length; i++) {
                  const part = parts[i];
                  if (i === parts.length - 1) {
                    cur[part] = obj[k];
                  } else {
                    cur[part] = cur[part] || {};
                    cur = cur[part];
                  }
                }
              } else {
                newObj[cleanKey] = obj[k];
              }
            }
            return newObj;
          });
          newArr = newArr.filter((obj) => Object.keys(obj).length > 0);

          // If out[baseKey] already exists and is an array, update its items
          if (Array.isArray(out[baseKey])) {
            let idx = 0;
            for (const newObj of newArr) {
              if (out[baseKey][idx]) {
                Object.assign(out[baseKey][idx], newObj);
              } else {
                out[baseKey][idx] = newObj;
              }
              idx += 1;
            }
          } else {
            out[baseKey] = newArr.length === 0 ? [] : newArr;
          }
          delete out[key];
        }
      }
    }
  }
};

const formatGroupPath = (groupPathGroup, out, values, enableWhenOk, extractItemProperties, it) => {
  const groupName = groupPathGroup.split('[')[0];
  out[groupName] = out[groupName] || [];
  const obj = {};
  let hasNonTextValue = false;
  for (const child of it.item || []) {
    if (isItemEnabled(child, enableWhenOk, extractItemProperties)) {
      let childPath = getCodeValue(child, 'backend:path');
      let textValue = null;
      const staticBackendPath = getCodeValue(child, 'backend:path:staticText');
      if (!childPath && staticBackendPath) {
        ({ childPath, textValue } = parseStaticBackendPath(staticBackendPath));
      }
      const key = childPath ? childPath.split('.').pop().replace('[]', '') : child.linkId;
      if (textValue !== null && textValue !== undefined) {
        obj[key] = textValue;
      } else {
        obj[key] = values[child.linkId];
        if (obj[key] !== undefined && obj[key] !== null && String(obj[key]).trim() !== '') {
          hasNonTextValue = true;
        }
      }
    }
  }
  // Only push if there is at least one non-text value
  if (hasNonTextValue) {
    out[groupName].push(obj);
  }
};

// Find all repeating groups and merge their arrays into output
const findRepeatingGroupPaths = (item, values, out, enableWhenOk, extractItemProperties) => {
  if (item.repeats && isItemEnabled(item, enableWhenOk, extractItemProperties)) {
    const arr = extractRepeatingGroupArrayDynamic(values, item);
    if (arr.length > 0) {
      out[item.linkId] = arr;
    }
  }
  const groupPathGroup = getCodeValue(item, 'backend:path:group');
  if (groupPathGroup) {
    formatGroupPath(groupPathGroup, out, values, enableWhenOk, extractItemProperties, item);
  }
};

export function buildBackendPayload(questionnaire, values, enableWhenOk, extractItemProperties, context = {}) {
  const { commonStore = {} } = context;
  const { brand = {}, config = {} } = commonStore;

  const out = {
    incidentDate: config.incidentDate ? dayjs().format('MM/DD/YYYY') : '',
    dateReceived: dayjs().format('MM/DD/YYYY'),
    planName: brand.name ?? null,
    market: (brand.state?.code || brand.state) ?? null,
    incidentRecordNumber: '',
    ...(config.application && { application: brand.spaceId ? 'Availity': 'Public' }),
    ...(config.lob && { lob: '' }),
    ...(config.extReqId && { extReqId: '' }),
    ...(config.whoCausedIncident && { whoCausedIncident: '' })
  };

  // Build a quick {groupLinkId -> pageCategory} map for questionCategory fallback (removed unused variable)
  const top = Array.isArray(questionnaire?.item) ? questionnaire.item : [];

  // Modern walk function using for...of
  const walk = (items, currentPageTitle) => {
    if (!Array.isArray(items)) return;
    for (const it of items) {
      if (!it || isDisplay(it)) {
        // skip this item
      } else {
        const nextPageTitle =
          isGroup(it) && getItemControl(it) === 'page' ? it.text || currentPageTitle : currentPageTitle;
        if (isGroup(it)) {
          const groupPathGroup = getCodeValue(it, 'backend:path:group');
          if (groupPathGroup || it.repeats === true) {
            findRepeatingGroupPaths(it, values, out, enableWhenOk, extractItemProperties);
          } else {
            walk(it.item, nextPageTitle);
          }
        } else {
          // 1) Map direct payload fields and array fields
          const fetchedCodes = getBackendPathCode(it);
          for (const fetchedCode of fetchedCodes) {
            switchCaseForToSetBackendPathValues(it, values, out, context, fetchedCode);
          }
          // 2) Attachments mapping logic
          setAttachmentsBackendPath(it, values, out);
          // 3) Map questions[] entries
          setQuestionsBackendPathValues(it, values, out);
        }
      }
    }
  };
  let isMoreThanOneFlowGroup = false;
  // Traverse top-level groups (pages or not)
  for (const section of top) {
    const pageTitle = isPageGroup(section) ? section.text || 'General' : null;
    const checkMoreFlows = !!getCodeValue(section, 'backend:path:ismoreThanoneflowgroup');
    if (checkMoreFlows) {
      isMoreThanOneFlowGroup = true;
    }
    if (isMoreThanOneFlowGroup) {
      if (isItemEnabled(section, enableWhenOk, extractItemProperties)) {
        walk(section.item ?? [], pageTitle);
      }
    } else {
      walk(section.item ?? [], pageTitle);
    }
  }
  // Post-process: flatten keys for repeating group arrays, including nested objects
  postProcessRepeatingGroups(out);
  return out;
}
